import {useState, useMemo} from 'react';
import {Table, Button, Dropdown, Tag, Tooltip, Alert} from 'antd';
import {useQuery, useMutation} from '@apollo/client';
import {GET_ALL_BROKERS} from 'graphql/queries/stocks';
import {DELETE_BROKER} from 'graphql/mutations/stocks';
import {toast} from 'react-toastify';
import styles from './brokerBackOffice.module.scss';
import {BrokerModal} from './BrokerModal';
import {ConfirmModal} from '../ViewOrders/HandleOrders';
import dotsIcon from 'icons/dots.svg';

export const BrokersTab = () => {
  const [brokerModal, setBrokerModal] = useState({
    open: false,
    brokerData: null,
  });

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    brokerId: null,
    brokerName: null,
  });

  const {
    data: getAllBrokers,
    loading: loadingAllBrokers,
    refetch,
  } = useQuery(GET_ALL_BROKERS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [deleteBroker, {loading: deleteLoading}] = useMutation(DELETE_BROKER, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  // Broker columns for the Brokers tab
  const brokerColumns = useMemo(
    () => [
      {
        title: 'Name',
        key: 'name',
        dataIndex: 'name',
        render: (name, record) => (
          <>
            {name || '-'}
            {record.routable === false && (
              <Tooltip title="Fund manager or contact-only record: never assigned an order.">
                <Tag style={{marginLeft: 8}}>Not routable</Tag>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        title: 'Asset Class',
        key: 'assetClass',
        dataIndex: 'assetClass',
        render: assetClass =>
          assetClass ? assetClass.replace(/_/g, ' ') : '-',
      },
      {
        // A broker record serves exactly one venue, so the Daba master appears
        // once per venue. Without this column those rows look identical.
        title: 'Exchange',
        key: 'exchangeCode',
        dataIndex: 'exchangeCode',
        render: (exchangeCode, record) =>
          exchangeCode || record.routable === false ? (
            exchangeCode || '-'
          ) : (
            <Tooltip title="Routing matches a broker's exchange exactly, so a broker with no exchange is never assigned an order. Edit it and set one.">
              <Tag color="red">No venue</Tag>
            </Tooltip>
          ),
      },
      {
        title: 'Currency',
        key: 'currency',
        dataIndex: 'currency',
        render: currency => currency || '-',
      },
      {
        // At most one master per asset class per venue; with two, selection
        // falls through to a non-master and the choice becomes arbitrary.
        title: 'Master',
        key: 'master',
        dataIndex: 'master',
        render: master => (master ? 'Yes' : 'No'),
      },
      {
        title: 'Email',
        key: 'emails',
        render: (_, record) => {
          // Old records only have the legacy single `email`.
          const addresses = record.emails?.length
            ? record.emails.map(entry => entry.email)
            : [record.email].filter(Boolean);
          if (!addresses.length) return '-';
          const extra = addresses.length - 1;
          return (
            <>
              {addresses[0]}
              {extra > 0 && (
                <Tooltip title={addresses.slice(1).join(', ')}>
                  <Tag style={{marginLeft: 8}}>+{extra}</Tag>
                </Tooltip>
              )}
            </>
          );
        },
      },
      {
        title: 'Statement URL',
        key: 'statementUrl',
        dataIndex: 'statementUrl',
        render: url =>
          url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              View Statement
            </a>
          ) : (
            '-'
          ),
      },
      {
        title: 'History URL',
        key: 'historyUrl',
        dataIndex: 'historyUrl',
        render: url =>
          url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              View History
            </a>
          ) : (
            '-'
          ),
      },
      {
        title: 'Cash Balance URL',
        key: 'cashUrl',
        dataIndex: 'cashUrl',
        render: url =>
          url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              View Cash Balance
            </a>
          ) : (
            '-'
          ),
      },
      {
        title: 'Portfolio URL',
        key: 'portfolioUrl',
        dataIndex: 'portfolioUrl',
        render: url =>
          url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              View Portfolio
            </a>
          ) : (
            '-'
          ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Edit',
                  onClick: () => {
                    setBrokerModal({
                      open: true,
                      brokerData: record,
                    });
                  },
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  danger: true,
                  onClick: () => {
                    setDeleteModal({
                      open: true,
                      brokerId: record.id,
                      brokerName: record.name,
                    });
                  },
                },
              ],
            }}>
            <div>
              <img src={dotsIcon} alt="dots" />
            </div>
          </Dropdown>
        ),
      },
    ],
    [],
  );

  const brokers = useMemo(
    () => getAllBrokers?.getBrokers?.data || [],
    [getAllBrokers],
  );

  // Two things silently break routing, and neither is visible row by row.
  const warnings = useMemo(() => {
    const messages = [];

    // Non-routable records (fund managers) never take orders, venue or not.
    const unrouted = brokers.filter(
      broker => broker.routable !== false && !broker.exchangeCode,
    );
    if (unrouted.length) {
      messages.push(
        `${unrouted.length} broker${
          unrouted.length > 1 ? 's have' : ' has'
        } no exchange set and will never be assigned an order: ${unrouted
          .map(broker => broker.name)
          .join(', ')}.`,
      );
    }

    // At most one master per asset class per venue — with two, selection falls
    // through to a non-master and which broker gets the order is arbitrary.
    const masterCounts = {};
    brokers
      .filter(broker => broker.master && broker.exchangeCode)
      .forEach(broker => {
        const key = `${broker.assetClass} @ ${broker.exchangeCode}`;
        masterCounts[key] = (masterCounts[key] || 0) + 1;
      });
    const duplicated = Object.keys(masterCounts).filter(
      key => masterCounts[key] > 1,
    );
    if (duplicated.length) {
      messages.push(
        `More than one master broker for ${duplicated.join(
          ', ',
        )}. Selection expects at most one, so the choice becomes arbitrary.`,
      );
    }

    return messages;
  }, [brokers]);

  return (
    <>
      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{marginBottom: 16}}
          message="Brokers that cannot take orders"
          description={warnings.map(warning => (
            <div key={warning}>{warning}</div>
          ))}
        />
      )}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <Button
          type="primary"
          onClick={() =>
            setBrokerModal({
              open: true,
              brokerData: null,
            })
          }>
          Create Broker
        </Button>
      </div>
      <div className={styles.tableContainer}>
        <Table
          rowKey="id"
          columns={brokerColumns}
          loading={loadingAllBrokers}
          className="antd-table"
          dataSource={brokers}
          pagination={false}
        />
      </div>
      <BrokerModal
        isOpen={brokerModal.open}
        onClose={() => setBrokerModal({open: false, brokerData: null})}
        brokerData={brokerModal.brokerData}
        refetch={refetch}
      />
      <ConfirmModal
        openModal={deleteModal.open}
        setOpenModal={() =>
          setDeleteModal({open: false, brokerId: null, brokerName: null})
        }
        title="Delete Broker"
        confirmText={`Are you sure you want to delete ${
          deleteModal.brokerName || 'this broker'
        }? This action cannot be undone.`}
        loading={deleteLoading}
        handleOperation={() => {
          deleteBroker({
            variables: {
              brokerId: deleteModal.brokerId,
            },
          })
            .then(({data}) => {
              if (data?.deleteBroker?.__typename === 'Error') {
                toast.error(data.deleteBroker.message);
              } else {
                toast.success(
                  data?.deleteBroker?.message || 'Broker deleted successfully',
                );
                setDeleteModal({open: false, brokerId: null, brokerName: null});
                refetch();
              }
            })
            .catch(error => {
              toast.error(error.message || 'Something went wrong');
            });
        }}
      />
    </>
  );
};
